import { useEffect, useState, type ReactNode } from 'react';
import { Boxes, Calendar, Layers, RefreshCw, Sliders, Tag, Truck } from 'lucide-react';
import { Accordion, Badge, DataState } from '@/components/ui';
import { Proveedores } from './Proveedores';
import { PageHeader } from '@/components/layout/PageHeader';
import { useForecast, useSettings, useUpdateSettings } from '@/lib/hooks';
import { formatCop } from '@shared/format';
import { cn } from '@/lib/cn';
import { ProductConfigTable } from './ProductConfigTable';
import { CategoryParams } from './CategoryParams';
import type { ForecastResumen, Settings } from '@shared/types';

const CALENDAR_VARIABLES = [
  { name: 'Temporada Alta — Fin de Año', period: 'Nov – Dic', impact: '+38% demanda proyectada', tone: 'success' as const },
  { name: 'Día Sin IVA', period: 'Jun · Oct', impact: '+62% pico de 3 días', tone: 'success' as const },
  { name: 'Semana Santa', period: 'Abr', impact: '−14% retail / +9% ecommerce', tone: 'info' as const },
  { name: 'Temporada Escolar', period: 'Ene – Feb', impact: '+21% categorías clave', tone: 'success' as const },
  { name: 'Vacaciones Mitad de Año', period: 'Jun – Jul', impact: '−8% demanda base', tone: 'warning' as const },
];

const PROMOTIONS = [
  { name: 'Black Friday', period: '28 nov – 1 dic', discount: '−30%', uplift: '+45% uds' },
  { name: 'CyberLunes', period: '4 dic', discount: '−25%', uplift: '+38% uds' },
  { name: 'Aniversario Marca', period: '15 – 20 sep', discount: '−20%', uplift: '+27% uds' },
];

export default function SettingsPage() {
  const settings = useSettings();
  const forecast = useForecast();
  const update = useUpdateSettings();

  return (
    <DataState query={settings}>
      {(data) => (
        <div className="space-y-6">
          <PageHeader
            title="Configuración del Sistema"
            subtitle="Parámetros que usa el motor para calcular stock de seguridad y punto de reorden, tiempos logísticos por producto, variables de calendario y promociones. Cada cambio queda en el log de auditoría."
          />

          <div className="space-y-4">
            <Accordion
              icon={<Sliders className="h-4 w-4" />}
              title="Parámetros Globales"
              subtitle="Valores base que usa el motor cuando un producto o una categoría no tienen los suyos"
              defaultOpen
            >
              <GlobalParams settings={data} onUpdate={(p) => update.mutate(p)} />
            </Accordion>

            <Accordion
              icon={<Layers className="h-4 w-4" />}
              title="Nivel de Servicio por Categoría"
              badge={data.categories.length}
              subtitle="Cuánto cuidar el quiebre en cada categoría. Lo que no configures usa los valores globales de arriba."
            >
              <CategoryParams settings={data} />
            </Accordion>

            <Accordion
              icon={<Truck className="h-4 w-4" />}
              title="Proveedores y tiempos de espera"
              subtitle="Cuánto tarda cada proveedor en entregar, como rango, y con cuánto margen calcular. De acá salen las fechas y las cantidades de compra."
            >
              <Proveedores />
            </Accordion>

            <Accordion
              icon={<Boxes className="h-4 w-4" />}
              title="Configuración por Producto"
              badge={data.productConfigCount}
              subtitle="Valor unitario, tiempo de producción y MOQ por SKU. Se edita haciendo click en el valor, o de a varios seleccionando y usando la barra de lote."
            >
              <ProductConfigTable />
            </Accordion>

            <Accordion
              icon={<Calendar className="h-4 w-4" />}
              title="Variables de Calendario"
              badge={data.calendarVariableCount}
              subtitle="Eventos, temporadas y fechas que ajustan la demanda proyectada del modelo"
            >
              <ul className="space-y-2">
                {CALENDAR_VARIABLES.map((v) => (
                  <li
                    key={v.name}
                    className="flex items-center justify-between gap-3 rounded-lg border border-hairline bg-subtle px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-strong">{v.name}</p>
                      <p className="text-2xs text-ink-faint">{v.period}</p>
                    </div>
                    <Badge tone={v.tone} size="sm">{v.impact}</Badge>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-2xs text-ink-faint">
                Las variables de calendario modulan la demanda base del modelo SARIMA según estacionalidad histórica.
              </p>
            </Accordion>

            <Accordion
              icon={<Tag className="h-4 w-4" />}
              title="Precios y Promociones"
              badge={data.promotionCount}
              subtitle="Registro de promociones para que el modelo ajuste la demanda proyectada"
            >
              <ul className="space-y-2">
                {PROMOTIONS.map((p) => (
                  <li
                    key={p.name}
                    className="flex items-center justify-between gap-3 rounded-lg border border-hairline bg-subtle px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-strong">{p.name}</p>
                      <p className="text-2xs text-ink-faint">{p.period}</p>
                    </div>
                    <div className="flex items-center gap-2 text-right">
                      <span className="text-xs font-semibold text-danger-text">{p.discount}</span>
                      <Badge tone="success" size="sm">{p.uplift}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-2xs text-ink-faint">
                Cada promoción registrada ajusta el uplift de demanda proyectada durante su vigencia.
              </p>
            </Accordion>

            <Accordion
              icon={<RefreshCw className="h-4 w-4" />}
              title="Motor de IA — Estado"
              subtitle="Con qué cuenta proyecta y con qué nivel de servicio"
            >
              <DataState query={forecast}>
                {(f) => (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {/* Decía "SARIMA + XGBoost · MAPE 9.4%" con los dos valores hardcodeados y
                        ningún modelo detrás. Ahora es lo que el motor realmente hace.

                        ⚠️ Acá había además el error medido en backtest. Se sacó el 2026-09-01
                        junto con el del Dashboard y el del forecast, por el mismo motivo: un
                        porcentaje de error en pantalla se lee como una garantía sobre lo que va
                        a pasar. Se sigue calculando y guardando en `forecast_sku` — es un dato
                        nuestro para comparar versiones del motor, no una promesa al cliente. */}
                    <EngineTile label="Método" value={metodoCorto(f.resumen)} sub="Depende de cómo vende cada SKU" />
                    <EngineTile
                      label="Con proyección"
                      value={`${f.resumen.conProyeccion} de ${f.resumen.productos}`}
                      sub="Productos con serie suficiente"
                    />
                    <EngineTile label="Nivel de servicio" value={`${f.meta.confidence}%`} sub="Banda de proyección" />
                  </div>
                )}
              </DataState>
            </Accordion>
          </div>
        </div>
      )}
    </DataState>
  );
}

function GlobalParams({ settings, onUpdate }: { settings: Settings; onUpdate: (p: Partial<Settings>) => void }) {
  const [serviceLevel, setServiceLevel] = useState(settings.serviceLevel);
  const [coverageTargetDays, setCoverageTargetDays] = useState(settings.coverageTargetDays);
  const [purchaseBudget, setPurchaseBudget] = useState(settings.purchaseBudget);
  const [reviewHourNum, setReviewHourNum] = useState(parseInt(settings.reviewHour, 10) || 7);

  useEffect(() => {
    setServiceLevel(settings.serviceLevel);
    setCoverageTargetDays(settings.coverageTargetDays);
    setPurchaseBudget(settings.purchaseBudget);
    setReviewHourNum(parseInt(settings.reviewHour, 10) || 7);
  }, [settings]);

  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
      {/* ⚠️ Los tres deslizadores arrancaban en un mínimo alto —nivel de servicio en 90,
          cobertura en 10, revisión en las 5 AM— y eso NO era una baranda técnica sino un
          supuesto sobre cómo compra el cliente. Charly: «tolerar mínimos de 0, actualmente el
          servicio no puede bajar de 90 % y eso me limita mucho». Ahora van de 0 al máximo real
          de cada uno.

          Y con un rango ancho un deslizador solo deja de alcanzar: 365 días en 600 px es medio
          día por píxel, así que poner un valor exacto es imposible. Por eso cada uno lleva
          además un CAMPO ESCRIBIBLE — el deslizador para tantear, el número para decidir. */}
      <ControlNumerico
        label="Nivel de Servicio Global (%)"
        valor={serviceLevel}
        min={0}
        max={99}
        sufijo="%"
        onCambio={setServiceLevel}
        onConfirmar={(v) => onUpdate({ serviceLevel: v })}
        izquierda="0% (sin colchón)"
        derecha="99% (máximo)"
        nota={
          serviceLevel <= 50
            ? 'Con 50 % o menos el colchón queda en CERO: cubrir la mitad de los casos es cubrir el promedio, y el promedio ya está en «lo que se vende durante la espera».'
            : undefined
        }
      />

      <ControlNumerico
        label="Días de Cobertura Objetivo (global)"
        valor={coverageTargetDays}
        min={0}
        max={365}
        sufijo="d"
        onCambio={setCoverageTargetDays}
        onConfirmar={(v) => onUpdate({ coverageTargetDays: v })}
        izquierda="0 días"
        derecha="365 días (un año)"
        nota={
          coverageTargetDays === 0
            ? 'En 0 se repone justo hasta el punto de reorden: cada compra deja el stock en el nivel que dispara la siguiente.'
            : undefined
        }
      />

      <Control label="Presupuesto para Compras (ciclo actual, COP)">
        <input
          type="number"
          value={purchaseBudget}
          onChange={(e) => setPurchaseBudget(Number(e.target.value))}
          onBlur={() => onUpdate({ purchaseBudget })}
          className="h-11 w-full rounded-xl border border-hairline bg-panel px-3 text-sm text-ink-strong focus:border-primary focus:outline-none"
        />
        <p className="mt-2 text-2xs text-ink-faint">
          {formatCop(purchaseBudget)} — tope de compras. Hoy es informativo: el motor todavía no prioriza las órdenes por presupuesto
        </p>
      </Control>

      <ControlNumerico
        label="Revisión Automática Diaria (hora)"
        valor={reviewHourNum}
        min={0}
        max={23}
        sufijo="hs"
        onCambio={setReviewHourNum}
        onConfirmar={(v) => onUpdate({ reviewHour: `${String(v).padStart(2, '0')}:00` })}
        izquierda="medianoche"
        derecha="11 de la noche"
      />
    </div>
  );
}

const SLIDER = 'mt-1 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-hover accent-primary';

/**
 * Deslizador + campo escribible, para un parámetro numérico.
 *
 * 📌 Los dos, y no uno: el deslizador da la noción de dónde está el valor dentro de lo posible
 * —que es la mitad de por qué existe— pero sobre un rango de 0 a 365 no se puede apuntar a un
 * número concreto. El campo resuelve eso sin sacar la referencia visual.
 *
 * ⚠️ El valor se GUARDA al soltar el deslizador o al salir del campo, nunca en cada tecla: con
 * `onChange` cada dígito tipeado sería un PATCH, y escribir "150" mandaría también el 1 y el 15
 * — tres valores guardados, dos de ellos absurdos.
 */
function ControlNumerico({
  label,
  valor,
  min,
  max,
  sufijo,
  onCambio,
  onConfirmar,
  izquierda,
  derecha,
  nota,
}: {
  label: string;
  valor: number;
  min: number;
  max: number;
  sufijo?: string;
  onCambio: (v: number) => void;
  onConfirmar: (v: number) => void;
  izquierda: string;
  derecha: string;
  /** Qué significa el valor actual, cuando amerita decirlo (un extremo, por ejemplo). */
  nota?: string;
}) {
  // Se acota acá y no sólo en el server: el campo es de texto libre y alguien puede escribir
  // 5000. Guardar eso y que el server lo rechace deja la pantalla mostrando algo que no está.
  const acotar = (v: number) => Math.max(min, Math.min(max, Math.round(v)));

  return (
    <div>
      <div className="flex items-end justify-between gap-3">
        <span className="text-xs font-medium text-ink">{label}</span>
        <div className="flex items-baseline gap-1">
          <input
            type="number"
            min={min}
            max={max}
            value={valor}
            onChange={(e) => onCambio(e.target.value === '' ? min : Number(e.target.value))}
            onBlur={() => onConfirmar(acotar(valor))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            aria-label={label}
            className="h-9 w-20 rounded-lg border border-hairline bg-panel px-2 text-right text-xl font-bold text-ink-strong focus:border-primary focus:outline-none"
          />
          <span className="text-sm text-ink-muted">{sufijo ?? ''}</span>
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={Math.max(min, Math.min(max, valor))}
        onChange={(e) => onCambio(Number(e.target.value))}
        onMouseUp={() => onConfirmar(acotar(valor))}
        onTouchEnd={() => onConfirmar(acotar(valor))}
        aria-label={`${label} (deslizador)`}
        className={`${SLIDER} mt-3`}
      />
      <Ends left={izquierda} right={derecha} />
      {nota && <p className="mt-2 max-w-prosa text-2xs leading-relaxed text-ink-muted">{nota}</p>}
    </div>
  );
}

function Control({ label, big, children }: { label: string; big?: string; children: ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <label className="text-xs font-medium text-ink-muted">{label}</label>
        {big && <span className="text-xl font-bold text-ink-strong">{big}</span>}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Ends({ left, right }: { left: string; right: string }) {
  return (
    <div className="mt-1.5 flex items-center justify-between text-2xs text-ink-faint">
      <span>{left}</span>
      <span>{right}</span>
    </div>
  );
}

function EngineTile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className={cn('rounded-xl border border-hairline bg-subtle p-4')}>
      <p className="text-2xs uppercase tracking-wide text-ink-faint">{label}</p>
      <p className="mt-1 text-lg font-bold text-ink-strong">{value}</p>
      <p className="mt-0.5 text-2xs text-ink-faint">{sub}</p>
    </div>
  );
}

/** "Media 365 d (438) · sin serie (262)" — con qué se calculó y a cuántos SKU. */
function metodoCorto(resumen: ForecastResumen): string {
  const nombres: Record<string, string> = {
    media_365: 'Media 365 d', ninguno: 'sin serie',
  };
  const usados = resumen.porMetodo.filter((m) => m.metodo !== 'ninguno');
  if (!usados.length) return 'sin calcular';
  return usados.map((m) => `${nombres[m.metodo] ?? m.metodo} (${m.n})`).join(' · ');
}
